import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  Checkbox as MUICheckbox,
  type CheckboxProps,
  type TextFieldProps,
} from '@mui/material';
import { COLORS } from '../../constants';

/**
 * Renders a custom Checkbox component from a FormControlLabel.
 *
 * @param {string} label - The label text for the checkbox.
 * @param {boolean} required - Boolean that makes the checkbox required.
 * @param {string} helperError - The helper text to show when an error exists.
 * @param {string} border - String that indicates if the checkbox should have a border.
 * @param {boolean} forceIsChecked={checkboxState[0]} - Boolean that indicates if the checkbox is checked.
 * @return {React.JSX.Element} The rendered custom checkbox component.
 */
export const Checkbox = ({
  label,
  required,
  error,
  helperText,
  border,
  forceIsChecked,
  ...rest
}: CheckboxProps & {
  label?: string;
  required?: boolean;
  error?: TextFieldProps['error'];
  helperText?: TextFieldProps['helperText'];
  border?: string;
  forceIsChecked?: boolean;
}) => {
  return (
    <FormControl>
      {label ? (
        <FormControlLabel
          onClick={(e) => e.stopPropagation()}
          data-testid="checkbox"
          variant={error ? 'error' : 'checkbox'}
          sx={{
            alignItems: 'center',
            gap: 1,
            pr: 1,
            margin: '0px',
            '& > div': {
              display: 'flex',
            },
            border: border ? border : undefined,
          }}
          control={<MUICheckbox data-testid={`checkbox-${label}`} {...rest} />}
          required={required}
          label={label}
        />
      ) : (
        <MUICheckbox
          data-testid={`checkbox`}
          className={error ? 'checkboxError' : ''}
          {...rest}
          sx={{ width: 'fit-content', color: error ? COLORS.state.error : '' }}
          onClick={(e) => e.stopPropagation()}
          required={required}
        />
      )}
      <FormHelperText sx={{ margin: label ? '3px 14px 0px' : '0px' }} error={error}>
        {required && error && !forceIsChecked ? helperText : ''}
      </FormHelperText>
    </FormControl>
  );
};
